import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, ADMIN_EMAILS } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { toE164 } from '@/utils/phoneFormat';
import PhoneInput from '@/components/auth/PhoneInput';
import OtpInput from '@/components/auth/OtpInput';
import LanguageModal from '@/components/LanguageModal';
import { useLanguage } from '@/hooks/useLanguage';

interface ToastProps {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
}

// Flag emoji mapping for all 13 supported languages.
// Every code here MUST have a resource in src/i18n.ts — the picker is a
// promise. ht/bn/yi were removed 2026-08-06 (no locale files existed; they
// silently fell back to English) and return when properly translated.
const LANGUAGE_FLAGS: Record<string, { flag: string; name: string }> = {
  en: { flag: '🇺🇸', name: 'English' },
  es: { flag: '🇪🇸', name: 'Spanish' },
  fr: { flag: '🇫🇷', name: 'French' },
  de: { flag: '🇩🇪', name: 'German' },
  it: { flag: '🇮🇹', name: 'Italian' },
  pt: { flag: '🇧🇷', name: 'Portuguese' },
  'zh-CN': { flag: '🇨🇳', name: 'Mandarin' },
  ar: { flag: '🇸🇦', name: 'Arabic' },
  he: { flag: '🇮🇱', name: 'Hebrew' },
  hi: { flag: '🇮🇳', name: 'Hindi' },
  ru: { flag: '🇷🇺', name: 'Russian' },
  ja: { flag: '🇯🇵', name: 'Japanese' },
  ko: { flag: '🇰🇷', name: 'Korean' },
};

// Languages array for the modal (with native greetings)
const LANGUAGES_FOR_MODAL = [
  { code: 'en', text: '🇺🇸 Welcome', language: 'English' },
  { code: 'es', text: '🇪🇸 Bienvenido', language: 'Spanish' },
  { code: 'fr', text: '🇫🇷 Bienvenue', language: 'French' },
  { code: 'de', text: '🇩🇪 Willkommen', language: 'German' },
  { code: 'it', text: '🇮🇹 Benvenuto', language: 'Italian' },
  { code: 'pt', text: '🇧🇷 Bem-vindo', language: 'Portuguese' },
  { code: 'zh-CN', text: '🇨🇳 欢迎', language: 'Mandarin' },
  { code: 'ar', text: '🇸🇦 أهلاً', language: 'Arabic' },
  { code: 'he', text: '🇮🇱 ברוכים הבאים', language: 'Hebrew' },
  { code: 'hi', text: '🇮🇳 स्वागत', language: 'Hindi' },
  { code: 'ru', text: '🇷🇺 Добро пожаловать', language: 'Russian' },
  { code: 'ja', text: '🇯🇵 ようこそ', language: 'Japanese' },
  { code: 'ko', text: '🇰🇷 환영합니다', language: 'Korean' },
];

const LoginPage = () => {
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);

  // Two-step state (1 = entry form, 2 = OTP verify). Used by both signin and signup.
  const [step, setStep] = useState<1 | 2>(1);
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  // E.164 phone captured at step 1, used by step 2 to verify.
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);

  // Member number preview
  const [nextMemberNumber, setNextMemberNumber] = useState<number | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');

  // Language modal and bar
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isLanguageBarDismissed, setIsLanguageBarDismissed] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const { sendPhoneOtp, verifyPhoneOtp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Set default tab from URL params
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'signup') {
      setActiveTab('signup');
    }
  }, [searchParams]);

  // Fetch next member number when signup tab is active
  useEffect(() => {
    const fetchNextNumber = async () => {
      try {
        // RPC bypasses RLS to read the next member number. Lives in v2 as of
        // 2026-09-07, which is the client's default schema, so no qualifier.
        const { data, error } = await supabase.rpc('get_next_member_number');

        if (error) {
          console.error('Error fetching next member number:', error);
          setNextMemberNumber(null);
          return;
        }

        if (data !== null && data !== undefined) {
          setNextMemberNumber(data);
        } else {
          setNextMemberNumber(1);
        }
      } catch (err) {
        console.error('Error fetching next member number:', err);
        setNextMemberNumber(null);
      }
    };

    // Only fetch when signup tab is shown
    if (activeTab === 'signup') {
      fetchNextNumber();
    }
  }, [activeTab]);

  const safeToast = (props: ToastProps) => {
    if (typeof toast === 'function') {
      toast(props);
    } else {
      console.warn('Toast system unavailable:', props);
    }
  };

  const resetToStep1 = () => {
    setStep(1);
    setCodeDigits(['', '', '', '', '', '']);
    setPendingPhone(null);
  };

  // Sign In step 1 — send OTP. shouldCreateUser: false → unknown phone returns error.
  const handleSigninSendCode = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const e164 = toE164(phone);
      if (!e164) {
        throw new Error('Please enter a valid phone number');
      }

      const { error } = await sendPhoneOtp({ phone: e164, shouldCreateUser: false });

      if (error) {
        const msg = error.message?.toLowerCase() ?? '';
        if (msg.includes('not found') || msg.includes('signups not allowed') || msg.includes('user not')) {
          throw new Error('No account found for this number. Tap Sign Up to create one.');
        }
        throw error;
      }

      setPendingPhone(e164);
      setStep(2);
      safeToast({
        title: t('login.codeSent'),
        description: `${t('login.codeSentDesc')} ${phone}`,
      });
    } catch (error) {
      safeToast({
        title: t('login.accessDenied'),
        description: error instanceof Error ? error.message : 'Unable to send code',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Sign Up step 1 — collect name+email+phone, send OTP.
  const handleSignupSendCode = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!firstName.trim() || !lastName.trim()) {
        throw new Error('Please enter your first and last name');
      }
      if (!email.trim()) {
        throw new Error('Please enter your email');
      }

      const e164 = toE164(phone);
      if (!e164) {
        throw new Error('Please enter a valid phone number');
      }

      const { error } = await sendPhoneOtp({
        phone: e164,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
      });

      if (error) throw error;

      setPendingPhone(e164);
      setStep(2);
      safeToast({
        title: t('login.codeSent'),
        description: `${t('login.codeSentDesc')} ${phone}`,
      });
    } catch (error) {
      safeToast({
        title: t('login.signupFailed'),
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — verify OTP. Shared by signin and signup; verifyOtp establishes the session.
  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!pendingPhone) return;
    setLoading(true);

    try {
      const token = codeDigits.join('');
      const { data, error } = await verifyPhoneOtp({ phone: pendingPhone, token });
      if (error) throw error;
      if (!data?.user) throw new Error('Verification failed');

      const verifiedUser = data.user;

      // Resolve admin routing using either auth.users.email or metadata.email.
      const metaEmail = (verifiedUser.user_metadata as Record<string, unknown> | undefined)?.email;
      const effectiveEmail =
        verifiedUser.email ?? (typeof metaEmail === 'string' ? metaEmail : null);
      const isAdminByEmail = !!(
        effectiveEmail && ADMIN_EMAILS.includes(effectiveEmail.toLowerCase())
      );

      const { data: profile } = await ((supabase as any)
        .schema('v2')
        .from('profiles') as any)
        .select('first_name, is_admin')
        .eq('id', verifiedUser.id)
        .single();

      safeToast({
        title: `${t('login.phoneVerified')} ✓`,
        description: t('login.signingYouIn'),
      });

      if (profile?.is_admin === true || isAdminByEmail) {
        navigate('/admin');
      } else if (profile && profile.first_name) {
        navigate('/');
      } else {
        navigate('/profile');
      }
    } catch (error) {
      safeToast({
        title: t('login.verificationFailed'),
        description: error instanceof Error ? error.message : 'Invalid verification code',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingPhone) return;
    setLoading(true);
    try {
      const { error } = await sendPhoneOtp({
        phone: pendingPhone,
        ...(activeTab === 'signup'
          ? {
              firstName: firstName.trim() || undefined,
              lastName: lastName.trim() || undefined,
              email: email.trim() || undefined,
            }
          : { shouldCreateUser: false }),
      });
      if (error) throw error;

      setCodeDigits(['', '', '', '', '', '']);
      safeToast({
        title: t('login.newCodeSent'),
        description: t('login.checkYourPhone'),
      });
    } catch (error) {
      safeToast({
        title: t('login.resendFailed'),
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // OAuth Sign-In with Google
  const handleSignInWithGoogle = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error) {
      safeToast({
        title: 'Sign in failed',
        description: error instanceof Error ? error.message : 'Unable to sign in with Google',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  // OAuth Sign-In with Apple
  const handleSignInWithApple = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error) {
      safeToast({
        title: 'Sign in failed',
        description: error instanceof Error ? error.message : 'Unable to sign in with Apple',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
  };

  // Input styles
  const inputStyle = {
    fontFamily: 'var(--sans)',
    background: 'color-mix(in srgb, var(--cream) 72%, transparent)',
    border: '1px solid color-mix(in srgb, var(--gold-hover) 20%, transparent)',
  };

  const inputClassName = "w-full px-4 py-3 rounded-lg text-maslow-navy placeholder-[color-mix(in_srgb,var(--navy)_45%,transparent)] transition-all duration-200 focus:border-maslow-navy focus:ring-2 focus:ring-[color-mix(in_srgb,var(--navy)_10%,transparent)] focus:bg-white outline-none";

  const labelClassName = "block text-[9px] font-normal tracking-[0.24em] uppercase mb-1.5";
  const labelStyle = { color: 'color-mix(in srgb, var(--gold-hover) 85%, transparent)', fontFamily: 'var(--sans)' };

  const phonePrefixClassName =
    'w-[58px] flex-shrink-0 flex items-center justify-center rounded-lg text-[13px] text-maslow-navy';
  const phonePrefixStyle = {
    fontFamily: 'var(--sans)',
    background: 'color-mix(in srgb, var(--cream) 72%, transparent)',
    border: '1px solid color-mix(in srgb, var(--gold-hover) 20%, transparent)',
  };

  const otpBoxClassName =
    'w-[42px] h-[46px] text-center text-xl font-light rounded-lg transition-all focus:border-maslow-navy focus:ring-2 focus:ring-[color-mix(in_srgb,var(--navy)_10%,transparent)] focus:bg-white outline-none';
  const otpBoxStyle = {
    fontFamily: 'var(--sans)',
    color: 'var(--charcoal)',
    background: 'color-mix(in srgb, var(--cream) 72%, transparent)',
    border: '1px solid color-mix(in srgb, var(--gold-hover) 20%, transparent)',
  };

  const renderStepDots = () => (
    <div className="flex justify-center gap-1.5 mb-4">
      {[1, 2].map((s) => (
        <div
          key={s}
          className="w-1 h-1 rounded-full transition-all duration-300"
          style={{
            background: step === s ? 'var(--navy)' : 'color-mix(in srgb, var(--gold-hover) 25%, transparent)',
            transform: step === s ? 'scale(1.3)' : 'scale(1)',
          }}
        />
      ))}
    </div>
  );

  const renderOtpStep = (onSubmit: (e: FormEvent) => void) => (
    <motion.form
      key="otp"
      variants={stepVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onSubmit={onSubmit}
      className="space-y-4"
    >
      {renderStepDots()}

      <p className="text-[11px] text-[color-mix(in_srgb,var(--navy)_45%,transparent)] text-center leading-relaxed mb-2" style={{ fontFamily: 'var(--sans)' }}>
        {t('login.enterCodeNote')}
      </p>

      <OtpInput
        value={codeDigits}
        onChange={setCodeDigits}
        boxClassName={otpBoxClassName}
        boxStyle={otpBoxStyle}
      />

      <p className="text-[11px] text-[color-mix(in_srgb,var(--navy)_45%,transparent)] text-center" style={{ fontFamily: 'var(--sans)' }}>
        {t('login.didntGetIt')}{' '}
        <button
          type="button"
          onClick={handleResendCode}
          disabled={loading}
          className="text-maslow-navy hover:underline"
        >
          {t('login.resend')}
        </button>
      </p>

      <button
        type="submit"
        disabled={loading || codeDigits.some(d => !d)}
        className="w-full py-3.5 rounded-lg text-white uppercase tracking-[0.26em] text-[11px] font-normal transition-all hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-50"
        style={{ fontFamily: 'var(--sans)', background: 'var(--navy)', boxShadow: '0 4px 16px color-mix(in srgb, var(--blue) 20%, transparent)' }}
      >
        {loading ? (
          <motion.span animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
            ...
          </motion.span>
        ) : t('login.verifyAndEnter')}
      </button>

      <button
        type="button"
        onClick={resetToStep1}
        className="w-full py-3 rounded-lg text-[color-mix(in_srgb,var(--navy)_45%,transparent)] uppercase tracking-[0.2em] text-[11px] font-normal transition-all border hover:border-[color-mix(in_srgb,var(--navy)_30%,transparent)] hover:text-maslow-navy"
        style={{ fontFamily: 'var(--sans)', border: '1px solid color-mix(in srgb, var(--gold-hover) 22%, transparent)' }}
      >
        ← {t('login.back')}
      </button>
    </motion.form>
  );

  // Main Login/Signup Screen
  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4" style={{ background: 'linear-gradient(160deg, var(--cream) 0%, var(--cream-2) 40%, color-mix(in srgb, var(--cream-2) 92%, var(--navy)) 100%)' }}>
      {/* Warm cream background with light bloom */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, var(--cream) 0%, var(--cream-2) 40%, color-mix(in srgb, var(--cream-2) 92%, var(--navy)) 100%)' }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 60% at 30% 0%, color-mix(in srgb, var(--cream) 80%, transparent) 0%, transparent 60%)' }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 80% 20%, color-mix(in srgb, var(--cream) 40%, transparent) 0%, transparent 45%)' }} />

      {/* Subtle linen texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, color-mix(in srgb, var(--cream) 15%, transparent) 3px, color-mix(in srgb, var(--cream) 15%, transparent) 4px), repeating-linear-gradient(90deg, transparent, transparent 3px, color-mix(in srgb, var(--cream) 8%, transparent) 3px, color-mix(in srgb, var(--cream) 8%, transparent) 4px)',
        }}
      />

      {/* Dismissible Language Bar - Top */}
      <AnimatePresence>
        {!isLanguageBarDismissed && (
          <motion.div
            initial={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="absolute top-0 left-0 right-0 z-20 overflow-hidden"
          >
            <div
              className="flex items-center justify-center px-4 py-2.5 relative"
              style={{
                background: 'color-mix(in srgb, var(--gold-hover) 8%, transparent)',
                borderBottom: '1px solid color-mix(in srgb, var(--gold-hover) 15%, transparent)',
              }}
            >
              <button
                onClick={() => setIsLanguageModalOpen(true)}
                className="flex items-center gap-2 transition-all hover:opacity-70"
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '11px',
                  letterSpacing: '0.15em',
                  color: 'color-mix(in srgb, var(--navy) 55%, transparent)',
                  textTransform: 'uppercase',
                }}
              >
                <span>{LANGUAGE_FLAGS[language]?.flag || '🌐'}</span>
                <span>{LANGUAGE_FLAGS[language]?.name || 'English'}</span>
              </button>

              {/* Dismiss button */}
              <button
                onClick={() => setIsLanguageBarDismissed(true)}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-all hover:opacity-70"
                style={{
                  color: 'color-mix(in srgb, var(--navy) 45%, transparent)',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.div
        className="relative z-10 w-full max-w-[340px] flex flex-col items-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Logo */}
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-2.5 mt-12">
          {/* Wordmark */}
          <div
            className="text-[30px] font-medium tracking-[0.3em] uppercase mb-2"
            style={{ fontFamily: 'var(--serif)', color: 'var(--navy)' }}
          >
            Maslow
          </div>

          {/* Tagline */}
          <div
            className="text-[10px] font-normal tracking-[0.28em] uppercase"
            style={{ fontFamily: 'var(--sans)', color: 'color-mix(in srgb, var(--navy) 55%, transparent)', letterSpacing: '0.28em' }}
          >
            {t('hero.tagline')}
          </div>
        </motion.div>

        {/* Divider */}
        <motion.div
          variants={itemVariants}
          className="w-[1px] h-7 my-5"
          style={{ background: 'linear-gradient(to bottom, transparent, color-mix(in srgb, var(--gold-hover) 40%, transparent), transparent)' }}
        />

        {/* Card */}
        <motion.div
          variants={itemVariants}
          className="w-full rounded-xl p-7"
          style={{
            background: 'color-mix(in srgb, var(--cream) 52%, transparent)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid color-mix(in srgb, var(--cream) 60%, transparent)',
            boxShadow: '0 8px 32px color-mix(in srgb, var(--charcoal) 8%, transparent), inset 0 1px 0 color-mix(in srgb, var(--cream) 90%, transparent)',
          }}
        >
          {/* Tabs */}
          <div className="relative flex justify-center border-b border-[color-mix(in_srgb,var(--gold-hover)_18%,transparent)] mb-6">
            <button
              onClick={() => { setActiveTab('signin'); resetToStep1(); }}
              className={`flex-1 text-center pb-3 text-[10.5px] font-normal tracking-[0.24em] uppercase transition-colors ${
                activeTab === 'signin' ? 'text-maslow-navy' : 'text-[color-mix(in_srgb,var(--navy)_45%,transparent)]'
              }`}
              style={{ fontFamily: 'var(--sans)' }}
            >
              {t('login.signIn')}
            </button>
            <button
              onClick={() => { setActiveTab('signup'); resetToStep1(); }}
              className={`flex-1 text-center pb-3 text-[10.5px] font-normal tracking-[0.24em] uppercase transition-colors ${
                activeTab === 'signup' ? 'text-maslow-navy' : 'text-[color-mix(in_srgb,var(--navy)_45%,transparent)]'
              }`}
              style={{ fontFamily: 'var(--sans)' }}
            >
              {t('login.signUp')}
            </button>

            {/* Animated underline */}
            <motion.div
              className="absolute bottom-[-1px] h-[1px] bg-maslow-gold"
              animate={{
                left: activeTab === 'signin' ? '10%' : '60%',
                width: '30%',
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
          </div>

          <AnimatePresence mode="wait">
            {/* Sign In step 1 — phone only */}
            {activeTab === 'signin' && step === 1 && (
              <motion.form
                key="signin-1"
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onSubmit={handleSigninSendCode}
                className="space-y-4"
              >
                {renderStepDots()}

                <div>
                  <label className={labelClassName} style={labelStyle}>{t('login.mobileNumber')}</label>
                  <PhoneInput
                    value={phone}
                    onChange={setPhone}
                    required
                    prefixClassName={phonePrefixClassName}
                    prefixStyle={phonePrefixStyle}
                    inputClassName={inputClassName + ' flex-1'}
                    inputStyle={inputStyle}
                  />
                </div>

                <p className="text-[11px] text-[color-mix(in_srgb,var(--navy)_45%,transparent)] text-center leading-relaxed" style={{ fontFamily: 'var(--sans)' }}>
                  {t('login.smsNote')}
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-lg text-white uppercase tracking-[0.26em] text-[11px] font-normal transition-all hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-50"
                  style={{ fontFamily: 'var(--sans)', background: 'var(--navy)', boxShadow: '0 4px 16px color-mix(in srgb, var(--blue) 20%, transparent)' }}
                >
                  {loading ? (
                    <motion.span animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                      ...
                    </motion.span>
                  ) : t('login.sendCode')}
                </button>

                {/* Social Sign-In Divider */}
                <div className="flex items-center gap-3 mt-5">
                  <div className="flex-1 h-px bg-[color-mix(in_srgb,var(--gold-hover)_20%,transparent)]" />
                  <span className="text-[10px] text-[color-mix(in_srgb,var(--navy)_45%,transparent)] uppercase tracking-wider" style={{ fontFamily: 'var(--sans)' }}>{t('login.or')}</span>
                  <div className="flex-1 h-px bg-[color-mix(in_srgb,var(--gold-hover)_20%,transparent)]" />
                </div>

                {/* Social Sign-In Buttons */}
                <div className="space-y-2.5 mt-4">
                  <button
                    type="button"
                    onClick={handleSignInWithGoogle}
                    disabled={loading}
                    className="w-full py-3 rounded-lg flex items-center justify-center gap-2.5 transition-all hover:opacity-90 disabled:opacity-50"
                    style={{
                      fontFamily: 'var(--sans)',
                      background: 'color-mix(in srgb, var(--cream) 85%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--gold-hover) 20%, transparent)',
                      color: 'var(--charcoal)',
                      fontSize: '12px',
                    }}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {t('login.continueWithGoogle')}
                  </button>

                  <button
                    type="button"
                    onClick={handleSignInWithApple}
                    disabled={loading}
                    className="w-full py-3 rounded-lg flex items-center justify-center gap-2.5 transition-all hover:opacity-90 disabled:opacity-50"
                    style={{
                      fontFamily: 'var(--sans)',
                      background: '#000',
                      color: '#fff',
                      fontSize: '12px',
                    }}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    {t('login.continueWithApple')}
                  </button>
                </div>
              </motion.form>
            )}

            {/* Sign Up step 1 — name + email + phone */}
            {activeTab === 'signup' && step === 1 && (
              <motion.form
                key="signup-1"
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onSubmit={handleSignupSendCode}
                className="space-y-4"
              >
                {renderStepDots()}

                <div>
                  <label className={labelClassName} style={labelStyle}>{t('login.firstName')}</label>
                  <input
                    type="text"
                    placeholder={t('login.firstName')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={inputClassName}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label className={labelClassName} style={labelStyle}>{t('login.lastName', 'LAST NAME')}</label>
                  <input
                    type="text"
                    placeholder={t('login.lastName')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={inputClassName}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label className={labelClassName} style={labelStyle}>{t('login.email')}</label>
                  <input
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClassName}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label className={labelClassName} style={labelStyle}>{t('login.mobileNumber')}</label>
                  <PhoneInput
                    value={phone}
                    onChange={setPhone}
                    required
                    prefixClassName={phonePrefixClassName}
                    prefixStyle={phonePrefixStyle}
                    inputClassName={inputClassName + ' flex-1'}
                    inputStyle={inputStyle}
                  />
                </div>

                {/* Member number teaser */}
                {nextMemberNumber && (
                  <div
                    className="text-center text-[13px] italic"
                    style={{ fontFamily: 'var(--serif)', color: 'color-mix(in srgb, var(--navy) 45%, transparent)' }}
                  >
                    {t('login.memberNumberTeaser')} <span style={{ color: 'var(--navy)' }}>#{String(nextMemberNumber).padStart(5, '0')}</span>
                  </div>
                )}

                <p className="text-[11px] text-[color-mix(in_srgb,var(--navy)_45%,transparent)] text-center leading-relaxed" style={{ fontFamily: 'var(--sans)' }}>
                  {t('login.smsNote')}
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-lg text-white uppercase tracking-[0.26em] text-[11px] font-normal transition-all hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-50"
                  style={{ fontFamily: 'var(--sans)', background: 'var(--navy)', boxShadow: '0 4px 16px color-mix(in srgb, var(--blue) 20%, transparent)' }}
                >
                  {loading ? (
                    <motion.span animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                      ...
                    </motion.span>
                  ) : t('login.sendCode')}
                </button>
              </motion.form>
            )}

            {/* Step 2 — OTP verification (shared by signin and signup) */}
            {step === 2 && renderOtpStep(handleVerifyCode)}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <motion.p
          variants={itemVariants}
          className="text-[9.5px] text-[color-mix(in_srgb,var(--navy)_45%,transparent)] text-center mt-5 leading-relaxed"
          style={{ fontFamily: 'var(--sans)', letterSpacing: '0.06em' }}
        >
          {t('login.agreeNotice')}<br />
          <Link
            to="/terms-of-service"
            className="hover:text-maslow-navy transition-colors"
            style={{ color: 'color-mix(in srgb, var(--navy) 55%, transparent)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            {t('login.termsOfService')}
          </Link>
          {' & '}
          <Link
            to="/privacy-policy"
            className="hover:text-maslow-navy transition-colors"
            style={{ color: 'color-mix(in srgb, var(--navy) 55%, transparent)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            {t('login.privacyPolicy')}
          </Link>
        </motion.p>
      </motion.div>

      {/* Language Modal */}
      <LanguageModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        onSelect={(lang) => {
          setLanguage(lang);
          setIsLanguageModalOpen(false);
        }}
        languages={LANGUAGES_FOR_MODAL}
        selectedLanguage={language}
      />
    </div>
  );
};

export default LoginPage;
